import { ReactNode, createContext, useContext } from 'react';
import {
  Control,
  FieldPath,
  FieldValues,
  UseControllerReturn,
  useController,
} from 'react-hook-form';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import PrimitiveDateRangeInput, {
  DateRange,
} from '@/components/form/_primitives/dateRange/PrimitiveDateRangeInput.tsx';

type DateRangeFieldContextValue = {
  startCtl: UseControllerReturn<FieldValues, string>;
  endCtl: UseControllerReturn<FieldValues, string>;
  required: boolean;
  disabled: boolean;
};

const DateRangeFieldContext = createContext<
  DateRangeFieldContextValue | undefined
>(undefined);

const useDateRangeFieldContext = () => {
  const ctx = useContext(DateRangeFieldContext);
  if (!ctx) {
    throw new Error(
      'DateRangeInput.Start/End must be rendered inside DateRangeInput.Root',
    );
  }
  return ctx;
};

type RootProps<T extends FieldValues> = {
  startFieldName: FieldPath<T>;
  endFieldName: FieldPath<T>;
  control: Control<T>;
  required?: boolean;
  disabled?: boolean;
  children: ReactNode;
};

const Root = <T extends FieldValues>({
  startFieldName,
  endFieldName,
  control,
  required = false,
  disabled = false,
  children,
}: RootProps<T>) => {
  const startCtl = useController({
    control,
    name: startFieldName,
    rules: { required },
  });
  const endCtl = useController({
    control,
    name: endFieldName,
    rules: { required },
  });

  const value: DateRange = {
    from: startCtl.field.value ? new Date(startCtl.field.value) : undefined,
    to: endCtl.field.value ? new Date(endCtl.field.value) : undefined,
  };

  const handleChange = (range: DateRange) => {
    startCtl.field.onChange(range.from);
    endCtl.field.onChange(range.to);
  };

  return (
    <DateRangeFieldContext.Provider
      value={{
        startCtl: startCtl as unknown as UseControllerReturn<
          FieldValues,
          string
        >,
        endCtl: endCtl as unknown as UseControllerReturn<FieldValues, string>,
        required,
        disabled,
      }}
    >
      <PrimitiveDateRangeInput.Root
        value={value}
        onChange={handleChange}
        disabled={disabled}
      >
        {children}
      </PrimitiveDateRangeInput.Root>
    </DateRangeFieldContext.Provider>
  );
};

type SideProps = {
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
};

type SideInternalProps = SideProps & {
  side: 'start' | 'end';
};

const Side = ({
  side,
  label,
  required,
  disabled,
  error,
}: SideInternalProps) => {
  const {
    startCtl,
    endCtl,
    required: rootRequired,
    disabled: rootDisabled,
  } = useDateRangeFieldContext();
  const ctl = side === 'start' ? startCtl : endCtl;
  const errorMessage = error ?? ctl.fieldState.error?.message;
  const Component =
    side === 'start'
      ? PrimitiveDateRangeInput.Start
      : PrimitiveDateRangeInput.End;

  return (
    <div className="flex w-full flex-col items-start gap-1.5">
      {label && (
        <InputLabel
          label={label}
          required={required ?? rootRequired}
          disabled={disabled ?? rootDisabled}
        />
      )}
      <Component
        id={ctl.field.name}
        name={ctl.field.name}
        error={errorMessage}
        disabled={disabled ?? rootDisabled}
        onBlur={() => ctl.field.onBlur()}
      />
      <InputError error={errorMessage} />
    </div>
  );
};

const Start = (props: SideProps) => <Side {...props} side="start" />;
const End = (props: SideProps) => <Side {...props} side="end" />;

const DateRangeInput = { Root, Start, End };

export default DateRangeInput;
